import { AsyncPipe, DecimalPipe, NgIf } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Fitter } from '@sc/data-access-models';
import { FittersSandboxService } from '@sc/data-access-state-management';
import {
  FitterNameDisplayService,
  FitterNamePipe,
  PhoneNumberPipe,
} from '@sc/feature-contextual-display';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { callCostLabel } from '@sc/utils';
import { IconComponent } from '../icon';
import { RatingComponent } from '../rating';

@Component({
  selector: 'sc-fitter-infos-box',
  styleUrls: ['./fitter-infos-box.component.scss'],
  providers: [FitterNameDisplayService],
  encapsulation: ViewEncapsulation.None,
  imports: [
    NgIf,
    RatingComponent,
    IconComponent,
    PhoneNumberPipe,
    FitterNamePipe,
    TranslateModule,
    AsyncPipe,
    DecimalPipe,
  ],
  template: `
    <div class="section-info" *ngIf="fitter">
      <div
        class="sc-design-text-3-bold"
        role="heading"
        [attr.aria-level]="mainTitleLevel"
      >
        {{ fitter | fitterName | async }}
      </div>

      <div class="fitter-info" *ngIf="fittersSB.isNotItinerant(fitter)">
        <div class="info-element sc-design-text-3" *ngIf="!hideTimeAndDistance">
          <sc-icon name="road" specificName="fitter_infos_box_road"></sc-icon>
          <p>
            {{
              'GlobalApplicativeConstants.distance.interventionDistanceKm'
                | translate
                  : {
                      distance:
                        itineraryDistanceInMeters >= 0
                          ? (itineraryDistanceInMeters / 1000 | number: '1.0-1')
                          : '-'
                    }
            }}
          </p>
        </div>

        <div class="info-element sc-design-text-3" *ngIf="!hideTimeAndDistance">
          <sc-icon name="time"></sc-icon>
          <p>
            {{
              'GlobalApplicativeConstants.time.interventionTimeInMinutes'
                | translate
                  : {
                      minStr:
                        itineraryDurationInSeconds >= 0
                          ? (itineraryDurationInSeconds / 60 | number: '1.0-0')
                          : '-'
                    }
            }}
          </p>
        </div>
        <sc-rating
          [rating]="fitter?.google?.rating"
          [brandId]="fitter?.brandId"
        ></sc-rating>
      </div>

      <div
        class="fitter-info-line sc-design-text-3"
        *ngIf="fittersSB.isNotItinerant(fitter)"
      >
        <sc-icon name="map_location"></sc-icon>
        <p [innerHTML]="fittersSB.getCompleteAddress(fitter)"></p>
      </div>
      <div
        class="fitter-info-line sc-conf-display-fitter-phone"
        *ngIf="!!fitter?.phone"
      >
        <sc-icon name="phone"></sc-icon>
        <p *ngIf="!canCallFitter">
          {{ fitter.phone | phoneNumber | async }}
          <span class="callCostLabel">{{
            callCostLabel(fitter.phone) | translate
          }}</span>
        </p>

        <button
          *ngIf="canCallFitter"
          class="callable-phone sc-design-text-3"
          (click)="makeCall()"
        >
          <span
            >{{ fitter.phone | phoneNumber | async }}
            <span class="callCostLabel"
              >&nbsp;{{ callCostLabel(fitter.phone) | translate }}</span
            ></span
          >
        </button>
      </div>
    </div>
  `,
})
export class FitterInfosBoxComponent {
  @Input() fitter: Fitter;
  @Input() itineraryDistanceInMeters = 0;
  @Input() itineraryDurationInSeconds = 0;
  @Input() hideTimeAndDistance = false;
  @Input() canCallFitter = false;
  @Output() makeCallEvent = new EventEmitter<string>();
  @Input() mainTitleLevel = 4;
  callCostLabel = callCostLabel;
  constructor(
    public featureFlippingService: FeatureFlippingManagerService,
    public fittersSB: FittersSandboxService
  ) {}

  makeCall() {
    this.makeCallEvent.emit(this.fitter?.phone);
  }
}
